// Pure calculation engine — không phụ thuộc DOM hay nguồn data.
// Shape của rows/achievements/rtoItems khai ở ./domain.ts.
// weekEnding = NGÀY log (TA log theo ngày, nhiều dòng/tuần đều được;
// tên field giữ nguyên vì là cột SharePoint có sẵn).

import type {
  Row, Totals, FunnelKey, Rates, Achievement, RtoByPosition, RtoByRecruiter,
} from "./domain.ts";

export const KPI_BONUS: Record<string, number> = {
  "TL S1/S2 pass probation": 5000000,
  "IT S1/S2 pass probation": 3000000,
  "IT M1/M2 pass probation": 2000000,
  "IT J1/J2 pass probation": 1000000,
  "IT M/S service contract cycle": 500000,
  "Time-to-fill ≤ 30 days": 500000,
  "Non-IT pass probation": 500000,
};

export const INTERVIEW_TARGET = 40;
export const RTO_TARGET = 8;
export const INTERVIEW_BONUS = 1000000;
export const RTO_BONUS = 1000000;
export const TOPUP_BONUS = 500000;

const FUNNEL_KEYS: readonly FunnelKey[] =
  ["contacted", "responses", "applications", "interviews", "offers", "hires"];

// Thứ Hai đầu tuần ISO của 1 ngày — quy log-theo-ngày về "tuần có hoạt
// động" khi tính target 5 interviews/tuần (log CN của data cũ rơi đúng
// vào tuần nó thuộc về).
export function weekKey(dateStr: string): string {
  // Cast: precondition là "YYYY-MM-DD". Input rác vẫn throw RangeError ở
  // toISOString() đúng như trước khi có type — không đổi hành vi.
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d - ((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7)));
  return dt.toISOString().slice(0, 10);
}

const pct = (num: number, den: number): number =>
  (den > 0 ? Math.round((num / den) * 1000) / 10 : 0);

export type RowFilter = { fromDate?: string; toDate?: string; recruiter?: string };

// Generic: trả về đúng type row đã truyền vào, để chuỗi filter → totals →
// render không mất thông tin (VD field notes của Row).
export function filterRows<T extends { weekEnding: string; recruiter: string }>(
  rows: readonly T[],
  { fromDate, toDate, recruiter }: RowFilter = {},
): T[] {
  return rows.filter((r) =>
    (!fromDate || r.weekEnding >= fromDate) &&
    (!toDate || r.weekEnding <= toDate) &&
    (!recruiter || r.recruiter === recruiter));
}

export function totals(rows: readonly Row[]): Totals {
  const t: Totals = {
    contacted: 0, responses: 0, applications: 0, interviews: 0, offers: 0, hires: 0,
  };
  for (const r of rows) for (const k of FUNNEL_KEYS) t[k] += r[k] || 0;
  return t;
}

// Tỷ lệ chuyển đổi giữa các bậc liên tiếp của funnel.
export function rates(t: Totals): Rates {
  return {
    responseRate: pct(t.responses, t.contacted),
    applicationRate: pct(t.applications, t.responses),
    interviewRate: pct(t.interviews, t.applications),
    offerRate: pct(t.offers, t.interviews),
    hireRate: pct(t.hires, t.offers),
  };
}

export type OutcomeSlice = { key: string; label: string; count: number; pct: number };

export function outcomeDistribution(t: Totals): OutcomeSlice[] {
  // Các bậc rơi của funnel suy từ chênh lệch giữa các cột. Nhóm
  // "offer chưa thành hire" (Offers − Hires) không hiển thị — không kết luận
  // được là từ chối hay đang chờ. Interviews có thể > Applications (nhiều
  // vòng PV) → clamp 0 và bù phần chênh vào bậc kế trước.
  const raw: [string, string, number][] = [
    ["onboarded", "Onboarded", t.hires],
    ["failedInterview", "Failed Interview", t.interviews - t.offers],
    ["failedCV", "Failed CV", t.applications - t.interviews],
    ["notApplied", "Not Applied", t.responses - t.applications],
    ["noResponse", "No Response", t.contacted - t.responses],
  ];
  let carry = 0;
  for (let i = raw.length - 1; i >= 0; i--) {
    const slice = raw[i]!; // i luôn trong bounds
    slice[2] += carry;
    carry = 0;
    if (slice[2] < 0) { carry = slice[2]; slice[2] = 0; }
  }
  if (carry) raw[0]![2] = Math.max(0, raw[0]![2] + carry);
  const total = raw.reduce((s, [, , c]) => s + c, 0);
  return raw.map(([key, label, count]) => ({ key, label, count, pct: pct(count, total) }));
}

export type LeaderboardEntry = {
  recruiter: string; intake: number; offers: number; successRate: number;
};

export function leaderboard(rows: readonly Row[]): LeaderboardEntry[] {
  const by = new Map<string, {
    recruiter: string; intake: number; offers: number; applications: number;
  }>();
  for (const r of rows) {
    const g = by.get(r.recruiter) || { recruiter: r.recruiter, intake: 0, offers: 0, applications: 0 };
    g.intake += r.contacted || 0;
    g.offers += r.offers || 0;
    g.applications += r.applications || 0;
    by.set(r.recruiter, g);
  }
  return [...by.values()]
    .map(({ applications, ...g }) => ({ ...g, successRate: pct(g.offers, applications) }))
    .sort((a, b) => b.offers - a.offers || b.intake - a.intake);
}

// Parse tech stack từ title work item trên Azure Board.
// Title chuẩn: "<level> <Stack> (<ngôn ngữ>) / <mã KH> - <ứng viên> - ..."
// Level: M1, M2/S1, M1+, FTE J2, S1/S2... — bỏ prefix level rồi lấy phần
// trước dấu "(" (hoặc trước " / " nếu không có ngoặc).
export function stackFromTitle(title: string): string {
  let s = String(title).trim();
  s = s.replace(/^FTE\s+/i, "");
  s = s.replace(/^(?:[MSJ]\d\+?(?:\/[MSJ]\d\+?)*\s+)+/i, "");
  const cut = Math.min(
    ...[s.indexOf(" ("), s.indexOf(" / ")].filter((i) => i > 0),
  );
  if (Number.isFinite(cut)) s = s.slice(0, cut);
  return s.trim();
}

// Match title work item vào 1 position (từ danh sách Position của list).
// So khớp theo word-boundary với tên position + các alias; token dài hơn
// thắng (VD "BE Web" thắng "BE"). Trả về tên position hoặc null.
export function positionForTitle(
  title: string,
  positions: readonly string[],
  aliases: Record<string, readonly string[]> = {},
): string | null {
  const t = String(title);
  let best: string | null = null;
  let bestLen = 0;
  for (const pos of positions) {
    for (const token of [pos, ...(aliases[pos] || [])]) {
      const re = new RegExp(`(^|[^A-Za-z0-9])${token.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&")}($|[^A-Za-z0-9])`, "i");
      if (re.test(t) && token.length > bestLen) {
        best = pos;
        bestLen = token.length;
      }
    }
  }
  return best;
}

// Generic T: giữ nguyên type item trong `candidates` để UI vẽ card được
// (name/level/eng...) mà engine vẫn chỉ khai phụ thuộc vào `position`.
export type RtoBenchmarkRow<T> = {
  stack: string;
  rto: number;
  candidates: T[];
  gap: number;
  noTarget?: boolean;
};

// Benchmark KPI "luôn có min ứng viên đang chờ offer / active tech stack".
// activeStacks do Admin chọn (subset của Position) — mọi stack được chọn
// đều có hàng, kể cả 0 RTO. rtoItems: item từ list "TA Ready to Offer"
// (đã lọc status Ready), position là choice tường minh nên không cần parse;
// position không nằm trong activeStacks → nhóm "Khác" (không có target).
export function rtoBenchmark<T extends RtoByPosition>(
  rtoItems: readonly T[],
  { activeStacks = [], min = 4 }: { activeStacks?: readonly string[]; min?: number } = {},
): RtoBenchmarkRow<T>[] {
  if (!activeStacks.length) return [];
  const rows = new Map<string, RtoBenchmarkRow<T>>(
    activeStacks.map((s) => [s, { stack: s, rto: 0, candidates: [], gap: min }]),
  );
  const other: RtoBenchmarkRow<T> =
    { stack: "Khác", rto: 0, candidates: [], gap: 0, noTarget: true };
  for (const it of rtoItems) {
    const g = rows.get(it.position) || other;
    g.rto++;
    g.candidates.push(it);
  }
  const out = [...rows.values()]
    .map((g) => ({ ...g, gap: Math.max(0, min - g.rto) }))
    .sort((a, b) => b.gap - a.gap || a.stack.localeCompare(b.stack));
  if (other.rto > 0) out.push(other);
  return out;
}

// Validate 1 dòng nhập funnel. Trả về null nếu hợp lệ, ngược lại là thông
// báo lỗi tiếng Việt. KHÔNG ràng buộc thứ tự giữa các stage: data là flow
// theo tuần, ứng viên chuyển stage lệch tuần là chuyện bình thường (offer
// tuần này cho người interview tuần trước, hire tuần này cho offer tuần
// trước...) nên Offers > Interviews trong cùng 1 tuần vẫn hợp lệ.
export function validateFunnelEntry(v: Record<string, unknown>): string | null {
  const fields: [FunnelKey, string][] = [
    ["contacted", "Contacted"], ["responses", "Responses"], ["applications", "Applications"],
    ["interviews", "Interviews"], ["offers", "Offers"], ["hires", "Hires"],
  ];
  for (const [key, label] of fields) {
    const n = v[key];
    if (typeof n !== "number" || Number.isNaN(n)) return `${label}: cần nhập số.`;
    if (n < 0) return `${label}: phải >= 0.`;
    if (!Number.isInteger(n)) return `${label}: phải là số nguyên.`;
  }
  return null;
}

export type PositionStatus = "filled" | "ontrack" | "red";

export type PositionSnapshot = Totals & {
  position: string;
  weekEnding: string;
  weeks: number;
  target: number;
  notes: string;
  status: PositionStatus;
  gap: number;
  stale: boolean;
  rates: Rates;
};

// Tiến độ từng vị trí đang tuyển trên tập rows được đưa vào (đã lọc theo
// date/recruiter filter): CỘNG DỒN mọi dòng log trong khoảng — đổi filter
// là số đổi theo. Log theo NGÀY: nhiều dòng cùng tuần ISO chỉ tính 1 tuần
// hoạt động; target phỏng vấn = interviewTarget × số tuần đó (không phạt
// tuần không log — checklist "missing positions" ở Admin lo việc đó).
// status: filled (có hire trong khoảng) / ontrack / red.
// notes: chỉ lấy của ngày log mới nhất (ghi chú cũ thường đã hết thời sự).
//
// `now` (YYYY-MM-DD) là mốc tính `stale`, BẮT BUỘC truyền vào chứ không gọi
// new Date() bên trong — để hàm này vẫn thuần và test không phụ thuộc ngày chạy.
//
// stale = vị trí chưa có log nào trong tuần ISO của `now`.
// Trước đây mốc là "ngày log mới nhất trong data", nghĩa là chỉ so các vị trí
// VỚI NHAU: cả team ngừng log thì mốc đứng yên theo, nhóm log muộn nhất vĩnh
// viễn không bị cảnh báo và dashboard trông như bình thường.
export function positionSnapshots(
  rows: readonly Row[],
  { interviewTarget = 5, now }: { interviewTarget?: number; now: string },
): PositionSnapshot[] {
  if (!rows.length) return [];
  const byPos = new Map<string, { rows: Row[]; weeks: Set<string>; latest: string }>();
  for (const r of rows) {
    const g = byPos.get(r.position) || { rows: [], weeks: new Set<string>(), latest: "" };
    g.rows.push(r);
    g.weeks.add(weekKey(r.weekEnding));
    if (r.weekEnding > g.latest) g.latest = r.weekEnding;
    byPos.set(r.position, g);
  }
  const order: Record<PositionStatus, number> = { red: 0, ontrack: 1, filled: 2 };
  return [...byPos.entries()].map(([position, g]): PositionSnapshot => {
    const t = totals(g.rows);
    const weeks = g.weeks.size;
    const target = interviewTarget * weeks;
    const notes = g.rows.filter((r) => r.weekEnding === g.latest)
      .map((r) => (r.notes || "").trim()).filter(Boolean).join(" · ");
    const status: PositionStatus =
      t.hires > 0 ? "filled" : t.interviews >= target ? "ontrack" : "red";
    return {
      position, weekEnding: g.latest, weeks, target, ...t, notes, status,
      gap: status === "red" ? target - t.interviews : 0,
      stale: weekKey(g.latest) < weekKey(now),
      rates: rates(t),
    };
  }).sort((a, b) => order[a.status] - order[b.status] || b.gap - a.gap);
}

export type KpiAchievement = Achievement & { bonus: number };

export type MonthlyKpiRow = {
  recruiter: string;
  interviews: number;
  rto: number;
  /** true = rto đếm từ log RTO thật; false = fallback cột Offers của funnel. */
  rtoLive: boolean;
  interviewsBonus: number;
  rtoBonus: number;
  topupBonus: number;
  achievements: KpiAchievement[];
  achievementsBonus: number;
  totalBonus: number;
};

// rtoItems (tùy chọn): item từ list "TA Ready to Offer". RTO của recruiter
// trong tháng = số ứng viên họ log với rtoDate thuộc tháng đó — đếm theo
// FLOW nên tháng nào cũng có số đúng của tháng ấy (kể cả khi ứng viên sau
// đó đã Offered/Hired/Rejected), và tháng đóng lại là số chốt cuối tháng.
// Không truyền rtoItems → fallback cột Offers của funnel (tương thích cũ).
export function monthlyKpi(
  rows: readonly Row[],
  achievements: readonly Achievement[],
  month: string,
  { rtoItems = null }: { rtoItems?: readonly RtoByRecruiter[] | null } = {},
): MonthlyKpiRow[] {
  const mRows = rows.filter((r) => r.weekEnding.startsWith(month));
  const mAch = achievements.filter((a) => a.month === month);
  const rtoBy = new Map<string, number>();
  for (const it of rtoItems || []) {
    if (!it.recruiter || it.recruiter === "—") continue;
    if (!(it.rtoDate || "").startsWith(month)) continue;
    rtoBy.set(it.recruiter, (rtoBy.get(it.recruiter) || 0) + 1);
  }
  const recruiters = new Set<string>([
    ...mRows.map((r) => r.recruiter),
    ...mAch.map((a) => a.recruiter),
    ...rtoBy.keys(),
  ]);
  return [...recruiters].map((recruiter): MonthlyKpiRow => {
    const own = mRows.filter((r) => r.recruiter === recruiter);
    const interviews = own.reduce((s, r) => s + (r.interviews || 0), 0);
    const rto = rtoItems
      ? rtoBy.get(recruiter) || 0
      : own.reduce((s, r) => s + (r.offers || 0), 0);
    const interviewsBonus = interviews >= INTERVIEW_TARGET ? INTERVIEW_BONUS : 0;
    const rtoBonus = rto >= RTO_TARGET ? RTO_BONUS : 0;
    const topupBonus = interviewsBonus && rtoBonus ? TOPUP_BONUS : 0;
    const ach = mAch.filter((a) => a.recruiter === recruiter)
      .map((a): KpiAchievement => ({ ...a, bonus: KPI_BONUS[a.kpiType] || 0 }));
    const achievementsBonus = ach.reduce((s, a) => s + a.bonus, 0);
    return {
      recruiter, interviews, rto, rtoLive: !!rtoItems,
      interviewsBonus, rtoBonus, topupBonus,
      achievements: ach, achievementsBonus,
      totalBonus: interviewsBonus + rtoBonus + topupBonus + achievementsBonus,
    };
  }).sort((a, b) => b.totalBonus - a.totalBonus);
}
